/**
 * Analytics Configuration
 * Centralized configuration for all analytics providers
 */

import type { AnalyticsConfig } from "@artha/types";

/**
 * ACG click/PostHog linkage is enabled by default. An explicit `false` remains
 * the emergency kill switch for a frontend deployment.
 */
export function isAcgLinkageExperimentEnabled(): boolean {
  return process.env.NEXT_PUBLIC_ACG_LINKAGE_EXPERIMENT_ENABLED !== "false";
}

/**
 * Get analytics configuration from environment variables
 */
export function getAnalyticsConfig(): AnalyticsConfig {
  console.debug("[Analytics] config", {
    posthogEnabled: process.env.NEXT_PUBLIC_POSTHOG_ENABLED,
    customerioEnabled: process.env.NEXT_PUBLIC_CUSTOMERIO_ENABLED,
    hasPosthogKey: !!process.env.NEXT_PUBLIC_POSTHOG_KEY,
  });
  return {
    posthog: {
      enabled: process.env.NEXT_PUBLIC_POSTHOG_ENABLED === "true",
      apiKey: process.env.NEXT_PUBLIC_POSTHOG_KEY || "",
      host: process.env.NEXT_PUBLIC_POSTHOG_HOST,
    },
    customerio: {
      enabled: process.env.NEXT_PUBLIC_CUSTOMERIO_ENABLED === "true",
      siteId: process.env.NEXT_PUBLIC_CUSTOMERIO_SITE_ID || "",
      apiKey: process.env.NEXT_PUBLIC_CUSTOMERIO_API_KEY || "",
    },
    amplitude: {
      enabled: process.env.NEXT_PUBLIC_AMPLITUDE_ENABLED === "true",
      apiKey: process.env.NEXT_PUBLIC_AMPLITUDE_API_KEY || "",
    },
    mixpanel: {
      enabled: process.env.NEXT_PUBLIC_MIXPANEL_ENABLED === "true",
      token: process.env.NEXT_PUBLIC_MIXPANEL_TOKEN || "",
    },
  };
}

/**
 * Validate analytics configuration
 */
export function validateAnalyticsConfig(config: AnalyticsConfig): {
  isValid: boolean;
  errors: string[];
} {
  const errors: string[] = [];

  // Validate PostHog
  if (config.posthog.enabled && !config.posthog.apiKey) {
    errors.push("PostHog API key is required when PostHog is enabled");
  }

  // Validate Customer.io
  if (config.customerio.enabled) {
    if (!config.customerio.siteId) {
      errors.push("Customer.io Site ID is required when Customer.io is enabled");
    }
    if (!config.customerio.apiKey) {
      errors.push("Customer.io API key is required when Customer.io is enabled");
    }
  }

  // Validate Amplitude
  if (config.amplitude?.enabled && !config.amplitude.apiKey) {
    errors.push("Amplitude API key is required when Amplitude is enabled");
  }

  // Validate Mixpanel
  if (config.mixpanel?.enabled && !config.mixpanel.token) {
    errors.push("Mixpanel token is required when Mixpanel is enabled");
  }

  return {
    isValid: errors.length === 0,
    errors,
  };
}

/**
 * Default development configuration
 */
export const DEV_ANALYTICS_CONFIG: AnalyticsConfig = {
  posthog: {
    enabled: true,
    apiKey: "dev-posthog-key",
    host: "https://app.posthog.com",
  },
  customerio: {
    enabled: false, // Disabled in dev by default
    siteId: "",
    apiKey: "",
  },
};

/**
 * Feature flags for analytics providers
 */
export const ANALYTICS_FEATURE_FLAGS = {
  ENABLE_CUSTOMERIO: "enable_customerio_tracking",
  ENABLE_AMPLITUDE: "enable_amplitude_tracking",
  ENABLE_MIXPANEL: "enable_mixpanel_tracking",
  ENABLE_DEBUG_LOGGING: "enable_analytics_debug_logging",
} as const;
